// 🎯 **자동 선택** — 제목·줄만 보고 어느 부품 조합으로 그릴지 정한다(2026-09-28 · 1단계 · 선택 UI 없음).
//
// ★순수 함수★ — 같은 제목·줄이면 언제나 같은 답. 내보내기·정밀편집·사진 굽기가 각자 이 함수를 불러도
//   같은 조합이 나오므로 «어느 부품인지»를 데이터에 실어 나를 필요가 없다(필드는 `motionStyle` 하나).
// 규칙(안착 인계서 6절 초안 → 여기서 확정 · 정본 지도 ADR 09-28):
//   ① 줄이 2개 이상이고 절반 이상이 순서어(«~하면·~고·~면·~서·완성·끝»)로 끝난다 → `step`(단계형)
//   ② 줄이 3개 이상이고 전부 짧다(항목형 · 24자 이하) → `check`(체크리스트 + 도장)
//   ③ 줄이 1개(제목 + 한 줄) → `pair`(두 줄 대구)
//   ④ 그 밖(줄 0개 · 2개 · 긴 문장 3개 이상) → 줄 0개는 `pair`(제목만), 2개는 `check`, 긴 문장은 `step`
//   ★반대편도 테스트가 잰다★ — 순서어가 절반 미만이면 step 이 아니고, 한 줄만 길어도 check 가 아니다.

export type MotionKitKind = "check" | "pair" | "step";

export interface MotionKitRecipe {
  kind: MotionKitKind;
  /** 배경 단색 — 종류마다 고정(목업 팔레트). 제목 해시로 돌리지 않는다: 같은 종류 = 같은 색이 읽기 쉽다. */
  color: string;
  /** 체크리스트 끝 도장 문구(check 만). */
  stamp?: string;
}

/** 항목형으로 보는 줄 길이 상한(글자 수 · 공백 포함). */
export const KIT_ITEM_MAX_CHARS = 24;

/** 순서어 — 줄 끝(문장부호 제거 뒤)이 이 중 하나로 끝나면 «다음 단계로 이어지는 말»로 본다. */
const ORDER_TAIL = /(하면|되면|보면|넣으면|올리면|누르면|고|면|서|완성|완료|끝)$/u;

const stripTail = (s: string): string => s.trim().replace(/[\s.!?…,:;~·]+$/u, "");

export function isOrderLine(line: string): boolean {
  const s = stripTail(line);
  return s.length > 0 && ORDER_TAIL.test(s);
}

export function isItemLine(line: string): boolean {
  const s = line.trim();
  return s.length > 0 && Array.from(s).length <= KIT_ITEM_MAX_CHARS;
}

export const KIT_COLORS: Readonly<Record<MotionKitKind, string>> = {
  check: "#2F5BFF",
  pair: "#FF4B3E",
  step: "#0FA37F",
};

export function pickMotionKitRecipe(input: { title: string; lines: readonly string[] }): MotionKitRecipe {
  const lines = input.lines.map((l) => String(l ?? "")).filter((l) => l.trim().length > 0);
  const n = lines.length;
  const finish = (kind: MotionKitKind): MotionKitRecipe =>
    kind === "check" ? { kind, color: KIT_COLORS.check, stamp: "완료" } : { kind, color: KIT_COLORS[kind] };
  if (n >= 2 && lines.filter(isOrderLine).length * 2 >= n) return finish("step");
  if (n >= 3 && lines.every(isItemLine)) return finish("check");
  if (n === 1) return finish("pair");
  if (n === 0) return finish("pair");
  if (n === 2) return finish("check");
  return finish("step");
}

/* ── 🥁 박자 배분 하한 · 어절 상한 (GNWORKS 하네스 흡수 3-1 · 2026-09-28) ───────────── */

/** 글자 블록이 화면에 머물러야 하는 최소 시간(초) = max(1.5, 글자 수 / 6). */
export function minHoldSec(text: string): number {
  return Math.max(1.5, Array.from(String(text ?? "").replace(/\s+/g, "")).length / 6);
}

/** 화면 글자 블록 한 줄의 어절 상한. 넘치면 `splitByWords` 가 줄을 나눈다. */
export const KIT_MAX_WORDS_PER_LINE = 6;

/** 어절 상한으로 먼저 줄을 나눈다(공백 기준 · 한 어절이 상한보다 길어도 자르지 않는다). 빈 줄은 버린다. */
export function splitByWords(text: string, maxWords = KIT_MAX_WORDS_PER_LINE): string[] {
  const out: string[] = [];
  for (const raw of String(text ?? "").split("\n")) {
    const words = raw.split(/\s+/u).filter(Boolean);
    for (let i = 0; i < words.length; i += maxWords) out.push(words.slice(i, i + maxWords).join(" "));
  }
  return out;
}

export interface BeatSchedule {
  /** 각 항목의 시작 시각(초 · 컷 시작 기준). */
  startsSec: number[];
  /** 각 항목이 얻은 노출 시간(초) — 마지막 항목은 컷 끝까지. */
  holdsSec: number[];
  /** 하한(`minHoldSec`)을 못 채운 항목 수 — 컷이 짧다는 신호(조립기는 그래도 전부 보여 준다). */
  starved: number;
}

/**
 * 🥁 **항목 n 개를 `[startSec, endSec]` 에 배분한다** — 각 항목의 하한 노출(`minHoldSec`)을 채우려 하고,
 * 구간이 모자라면 균등 압축한다(★모든 항목이 구간 안에 반드시 나온다★ — 완료 조건 ③이 하한보다 우선).
 * 마지막 항목은 `endSec - lastHold` 이전에 시작해 컷 끝까지 머문다. 순수 함수.
 */
export function scheduleBeats(texts: readonly string[], startSec: number, endSec: number): BeatSchedule {
  const n = texts.length;
  if (n === 0) return { startsSec: [], holdsSec: [], starved: 0 };
  const holds = texts.map(minHoldSec);
  const span = Math.max(0, endSec - startSec);
  const need = holds.reduce((a, b) => a + b, 0);
  const scale = need > 0 ? Math.min(1, span / need) : 1;
  const starts: number[] = [];
  let cursor = startSec;
  for (let i = 0; i < n; i++) {
    starts.push(cursor);
    cursor += holds[i] * scale;
  }
  const got = starts.map((s, i) => (i + 1 < n ? starts[i + 1] - s : endSec - s));
  return { startsSec: starts, holdsSec: got, starved: got.filter((g, i) => g + 1e-9 < holds[i]).length };
}
